import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useField } from 'informed';
import isEmail from 'validator/lib/isEmail';
import api from 'api';
import { useReduxState } from 'hooks';
import { Badge, Button, User } from 'components/common';
import { Icon } from 'components/common/icons';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import { SelectInput } from 'components/common/form/common';

const findEmailsRegex = /[,;\s]*[^\s,;@]+@[^\s@]+\.[^\s,;@]+[,;\s][,;\s]*/g;
const findExtraSeparatorsAtStartRegex = /^[<.,;'\\\s]*/;
const findExtraSeparatorsAtEndRegex = /[>.,;'\\\s]*$/;
const findLineBreaksRegex = /(\r\n|\n|\r)/gm;
const findSpecialCharactersRegex = /(Å|å|Ä|ä|Ö|ö|Ü|ü)/gm;

export interface EmailsProps {
  countNewUsers?: number;
  disabled?: boolean;
  disableCreatingNewAccounts?: boolean;
  disableOptions?: boolean;
  existingUsers?: number[];
  existingUsersOnly?: boolean;
  field: string;
  id?: string;
  isRequired?: boolean;
  onChange?: (value: any) => void;
  onUserChange?: (value: any) => void;
  style?: CSSProperties;
}

const Emails = (props: EmailsProps) => {
  const {
    messages,
    serverConfig: { enableUserSearch },
    theme,
    user
  } = useReduxState();

  const getUsersTimeout = useRef<NodeJS.Timeout>(undefined);

  const [inputValue, setInputValue] = useState('');
  const [valuesWithSpecialCharacters, setValuesWithSpecialCharacters] =
    useState([]);

  const { fieldState, fieldApi, render, ref, userProps } = useField({
    ...props
  });

  const {
    countNewUsers = 0,
    disabled,
    disableCreatingNewAccounts = false,
    disableOptions,
    existingUsers,
    existingUsersOnly = false,
    id,
    isRequired,
    onChange,
    onUserChange,
    style,
    ...params
  } = userProps;

  const fieldValue = fieldState.value as string[];

  useEffect(() => {
    return () => clearTimeout(getUsersTimeout.current);
  }, []);

  useEffect(() => {
    if (!fieldState.touched) setInputValue('');
  }, [fieldState.touched]);

  const getConvertedEmail = (emailAddress) => {
    return emailAddress
      .replace(/Å/g, 'a')
      .replace(/å/g, 'a')
      .replace(/Ä/g, 'a')
      .replace(/ä/g, 'a')
      .replace(/Ö/g, 'o')
      .replace(/ö/g, 'o')
      .replace(/Ü/g, 'u')
      .replace(/ü/g, 'u');
  };

  const getUserOptions = (value) => {
    let userIds = new Set<number>();
    if (existingUsers) userIds = new Set(existingUsers);

    clearTimeout(getUsersTimeout.current);
    return new Promise((resolve) => {
      getUsersTimeout.current = setTimeout(async () => {
        try {
          const response = await api.get(
            `/admin/users?search=${value}&role=user`
          );
          if (response.data && response.data.success) {
            const users = response.data.users
              .map((user) => ({
                label: <User user={user} />,
                value: user.email,
                id: user.id
              }))
              .filter(
                (user) =>
                  user.value &&
                  (existingUsersOnly
                    ? userIds.has(user.id)
                    : !userIds.has(user.id))
              );
            resolve(users);
          } else {
            resolve([]);
          }
        } catch (err) {
          resolve([]);
        }
      }, 500);
    });
  };

  const handleChange = (arr) => {
    const value =
      arr && arr.length ? arr.map((obj) => obj.value?.toLowerCase()) : null;
    fieldApi.setValue(value);

    if (value) {
      fieldApi.setError(undefined);
    } else if (!value && isRequired) {
      fieldApi.setError(messages.commonFieldRequired);
    }

    if (onChange) onChange(value);
  };

  const handleConvert = () => {
    const emails = (fieldValue || []).map((value) => {
      if (
        valuesWithSpecialCharacters.includes(value) &&
        value.match(findSpecialCharactersRegex)
      ) {
        return getConvertedEmail(value);
      }
      return value;
    });
    fieldApi.setValue(emails);
    setValuesWithSpecialCharacters([]);
  };

  const handleConvertCancel = () => {
    setValuesWithSpecialCharacters([]);
  };

  const handleInputChange = (currentValue, { action }) => {
    if (action === 'menu-close') return;

    let value = currentValue;
    if (action === 'input-blur') {
      value = inputValue + ' ';
    }

    const emailMatches = value.match(findEmailsRegex);

    const newEmails = [];

    if (emailMatches) {
      // handle each match
      value = value.replace(findEmailsRegex, (email, length, string) => {
        // remove extra separators
        const parsedEmail = email
          .replace(findExtraSeparatorsAtStartRegex, '')
          .replace(findExtraSeparatorsAtEndRegex, '');
        // validate email
        if (isEmail(parsedEmail, { domain_specific_validation: true })) {
          // filter out duplicates
          if (
            !(fieldValue || []).includes(parsedEmail.toLowerCase()) &&
            !newEmails.includes(parsedEmail.toLowerCase())
          ) {
            newEmails.push(parsedEmail.toLowerCase());
          }
          return '';
        } else {
          return email;
        }
      });
    }

    value = value.replace(findLineBreaksRegex, '').trim();

    const emails = [...(fieldValue || []), ...newEmails];

    const newValuesWithSpecialCharacters = newEmails.filter((newEmail) =>
      newEmail.match(findSpecialCharactersRegex) ? true : false
    );

    setInputValue(value);
    setValuesWithSpecialCharacters([
      ...valuesWithSpecialCharacters,
      ...newValuesWithSpecialCharacters
    ]);

    fieldApi.setValue(emails);
    fieldApi.setTouched(true);

    if (newEmails.length > 0 && onUserChange) {
      onUserChange(emails);
    }

    // Check if the field should display an error
    if (emails.length === 0 && !value.trim() && isRequired) {
      fieldApi.setError(messages.commonFieldRequired);
    } else {
      fieldApi.setError(undefined);
    }
  };

  const handleKeyDown = (ev) => {
    // prevent enter from submitting form
    if (ev.keyCode === 13) {
      // Only prevent default if there's no active option in the dropdown
      const hasActiveOption = ref.current?.state?.focusedOption;
      if (!hasActiveOption) {
        ev.preventDefault();
        // hack to check input value
        if (!ref.current.state.loadedOptions?.length) {
          handleInputChange(fieldValue, { action: 'input-blur' });
        }
      }
    }
  };

  return render(
    <>
      <div style={style} onKeyDown={handleKeyDown}>
        <SelectInput
          hideDropdownIndicator={true}
          id={id}
          inputId={`${id}-input`}
          isClearable={false}
          isDisabled={disabled}
          isMulti={true}
          isSearchable={true}
          loadingMessage={() => null}
          menuIsOpen={true}
          menuWidth="360px"
          noOptionsMessage={() => null}
          onChange={handleChange}
          placeholder="user1@example.com, user2@example.com, ..."
          selectRef={ref}
          value={
            fieldValue
              ? fieldValue.map((value) => ({ label: value, value: value }))
              : null
          }
          {...(!disableCreatingNewAccounts
            ? { inputValue, onInputChange: handleInputChange }
            : {})}
          {...(user?.roles?.includes('superadmin') ||
          (enableUserSearch && !disableOptions)
            ? { async: true, loadOptions: getUserOptions }
            : {})}
          {...params}
        />

        {fieldState.error ? (
          <div className="error">{fieldState.error}</div>
        ) : fieldValue?.length ? (
          <div className="emails-count">
            {fieldValue.length} {messages.settingsAddUsersEmails}
            {countNewUsers > 0
              ? `, ${countNewUsers} ${messages.commonNewUsers.toLowerCase()}`
              : null}
          </div>
        ) : null}

        <style jsx>{`
          .error {
            color: ${theme.dangerColor};
            font-size: 1.3rem;
            padding: 3px;
          }
          .emails-count {
            color: ${theme.textColorLighter};
            font-size: 1.3rem;
            padding: 3px;
          }
        `}</style>
      </div>

      {valuesWithSpecialCharacters?.length ? (
        <ModalPortal>
          <Modal
            onHide={handleConvertCancel}
            stopPropagation={true}
            title={messages.emailAddressesConvertTitle}
            visible={true}
            width="medium"
          >
            <ModalContent>
              <div className="convert">
                <div className="convert-info">
                  {messages.emailAddressesConvertInfo}
                </div>

                {valuesWithSpecialCharacters.map((emailAddress) => (
                  <div className="convert-email" key={emailAddress}>
                    <div className="convert-from">
                      <Badge
                        category="warning-light"
                        isRounded={false}
                        text={emailAddress}
                      />
                    </div>
                    <div className="convert-icon">
                      <Icon icon="arrow-right" />
                    </div>
                    <div className="convert-to">
                      <Badge
                        category="success-light"
                        isRounded={false}
                        text={getConvertedEmail(emailAddress)}
                      />
                    </div>
                  </div>
                ))}

                <style jsx>{`
                  .convert-info {
                    margin-bottom: 15px;
                  }
                  .convert-email {
                    align-items: center;
                    display: flex;
                    margin-top: 10px;
                  }
                  .convert-icon {
                    font-size: 1.2rem;
                    margin: 0 5px;
                  }
                `}</style>
              </div>
            </ModalContent>
            <ModalFooter>
              <Button
                category="light"
                onClick={handleConvertCancel}
                text={messages.emailAddressesConvertCancel}
              />
              <Button
                category="success"
                onClick={handleConvert}
                text={messages.emailAddressesConvertConfirm}
              />
            </ModalFooter>
          </Modal>
        </ModalPortal>
      ) : null}
    </>
  );
};

export default Emails;
